import * as React from 'react';
import {
  Grid,
  LinearProgress,
  Paper,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { Heading } from './common/Heading';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/env';
import { NavLink } from 'react-router-dom';
import Button from '@mui/material/Button';
import PaginationTable from './Pagination';
import removeButton from '../icons/remove.png';
import Box from '@mui/material/Box';

const Root = styled('div')`
    position: relative;

    .linear-bottom {
        position: absolute;
        bottom: 0;
        width: 100%;
        left: 0;
    }

    .linear-top {
        position: absolute;
        top: 0;
        width: 100%;
        left: 0;
    }
`;

export default function Users() {
  const [users, setUsers] = useState<{
    id: number,
    name: string,
    email: string,
    blank_password: boolean
  }[]>([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(20);
  const [orderBy, setOrderBy] = useState<string>('email');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const isAdmin = localStorage.getItem('is_admin') === 'true';
  if (!isAdmin) {
    window.location.href = '/login';
  }

  const resendInvite = async (id: number) => {
    await axios.post(
      `${getApiBaseUrl()}/users/resend-invite`, { id },
    ).then(() => {
      alert('Invite resent');
    }).catch(() => {
      alert('Failed to resend invite');
    });
  };

  const onRemoveUser = async (id: number, email: string) => {
    if (window.confirm(`Are you sure you want to remove user '${email}'?`)) {
      await axios.delete(
        `${getApiBaseUrl()}/users/${id}`
      ).then(() => {
        alert('User successfully removed');
        updateList()
      }).catch(() => {
        alert('Failed to remove this user');
      });
    }
  };

  const updateList = async () => {
    setLoading(true);
    axios.get(
      `${getApiBaseUrl()}/users`, {
        params: {
          limit, page, sort: orderBy ? `${orderBy},${order}` : undefined,
          search: searchQuery
        }
      }
    ).then(({ data: { items, meta } }) => {
      setLoading(false);
      setUsers(items);
        setTotalItems(meta.totalItems);
      }).catch(ex => {
        if (ex.message) {
          alert(ex.message);
        }
        console.error(ex);
      setLoading(false);
    });
    }

  useEffect(() => {
    const debounceTimer = setTimeout(updateList, 1000);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page, orderBy, order, searchQuery]);

  return (
    <React.Fragment>
      <Root>
        <Grid container style={{ paddingBottom: '15px', paddingLeft: '15px' }}>
          <Grid container alignItems="center" justifyContent={'space-between'}>
            <Grid item>
              <Heading>Users</Heading>
            </Grid>
          </Grid>
        </Grid>
      </Root>
      <Root>
        <TableContainer component={Paper} sx={{ maxWidth: 800 }}>
          <Grid container spacing={2} justifyContent={'space-between'}
                sx={{ marginBottom: 2, padding: 2 }}>
            <Grid item>
              <TextField
                label="Search in name or email"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                style={{ marginBottom: '15px' }}
              />
            </Grid>
            <Grid item>
              <Button href="/admin/users/add" variant={'contained'}>Add user</Button>
            </Grid>
          </Grid>
          <Box sx={{
            position: 'relative',
          }}>
          {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />}
          </Box>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderBy === 'name' ? order : false}
                  onClick={() => {
                    const isAsc = orderBy === 'name' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('name');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Name {orderBy === 'name' && (order === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'email' ? order : false}
                  onClick={() => {
                    const isAsc = orderBy === 'email' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('email');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Email {orderBy === 'email' && (order === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.email}
                  </TableCell>
                  <TableCell align="right">
                    <Grid container alignItems={'center'} justifyContent={'flex-end'} gap={1}>
                      {row.blank_password && (<Grid item>
                        <Button onClick={() => resendInvite(row.id)}>Resend invite</Button>
                      </Grid>)}
                      <Grid item>
                        <NavLink to={`/admin/users/edit/${row.id}`}>
                          <SvgIcon>
                            <EditIcon/>
                          </SvgIcon>
                        </NavLink>
                      </Grid>
                      <Grid item>
                        <span onClick={() => onRemoveUser(row.id, row.email)} style={{ cursor: 'pointer' }}>
                        <img width="28" src={removeButton} alt="remove button"/>
                      </span>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationTable
            defaultSize={20}
            busy={false}
            startPage={page - 1}
            count={totalItems}
            onChange={page => {
              setPage(page + 1);
            }}
            updateRowPerPage={totalPerPage => {
              setLimit(totalPerPage);
              setPage(1);
            }}></PaginationTable>
        </TableContainer>
      </Root>
    </React.Fragment>
  );
};
